import type { CheckoutStepIdentifier } from './constants'

const stepMs: Partial<Record<CheckoutStepIdentifier, number>> = {}
let totalActiveMs = 0
let currentStep: CheckoutStepIdentifier | null = null
let sessionActive = false
let activeSince: number | null = null
let listenersAttached = false

const isPageActive = () =>
  typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus()

const toSeconds = (ms: number) => Math.max(0, Math.round(ms / 1000))

function tick(): void {
  if (!sessionActive || activeSince == null) return
  const delta = Date.now() - activeSince
  if (delta > 0) {
    totalActiveMs += delta
    if (currentStep) stepMs[currentStep] = (stepMs[currentStep] ?? 0) + delta
  }
  activeSince = null
}

function syncClock(): void {
  if (!sessionActive) return
  tick()
  if (isPageActive()) activeSince = Date.now()
}

function attachListenersOnce(): void {
  if (listenersAttached || typeof document === 'undefined') return
  listenersAttached = true
  document.addEventListener('visibilitychange', syncClock)
  window.addEventListener('focus', syncClock)
  window.addEventListener('blur', syncClock)
}

function resetState(): void {
  tick()
  for (const key of Object.keys(stepMs) as CheckoutStepIdentifier[]) delete stepMs[key]
  totalActiveMs = 0
  currentStep = null
  sessionActive = false
  activeSince = null
}

export function startCheckoutV2ActiveTimeSession(): void {
  attachListenersOnce()
  resetState()
  sessionActive = true
  if (isPageActive()) activeSince = Date.now()
}

export function ensureCheckoutV2ActiveTimeSession(): void {
  attachListenersOnce()
  if (!sessionActive) {
    sessionActive = true
    if (isPageActive()) activeSince = Date.now()
  }
}

export function setCheckoutV2ActiveStep(step: CheckoutStepIdentifier | null): void {
  if (!sessionActive) {
    currentStep = step
    return
  }
  tick()
  currentStep = step
  if (isPageActive()) activeSince = Date.now()
}

export function flushAndGetCheckoutV2CompletionTiming(step: CheckoutStepIdentifier): {
  time_spent_seconds: number
  total_checkout_duration_seconds: number
} {
  tick()
  return {
    time_spent_seconds: toSeconds(stepMs[step] ?? 0),
    total_checkout_duration_seconds: toSeconds(totalActiveMs),
  }
}

export function __resetCheckoutV2ActiveTimeForTests(): void {
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', syncClock)
    window.removeEventListener('focus', syncClock)
    window.removeEventListener('blur', syncClock)
  }
  listenersAttached = false
  resetState()
}
